import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { RiAttachment2 } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useCallback, useRef, useState } from "react";
import useSWRMutation from "swr/mutation";
import { messageCacheKey } from "~/hooks/deals/useMessages";
import { messageCacheKey as vendorMessageCacheKey } from "~/hooks/vendors/deals/useMessages";
import type { Deal } from "~/models/Deal";
import { isSuccess } from "~/utils/api/api_handler";
import { createFile as createFileForCompany } from "~/utils/api/deals/Message";
import { createFile as createFileForVendor } from "~/utils/api/vendors/deals/Message";
import Styles from "./ChatPostFileButton.module.scss";

type Props = DeepReadonly<{
	deal: Deal;
	isVendor: boolean;
}>;

export function ChatPostFileButton({ deal, isVendor }: Props) {
	const { notify } = useNotification();
	const inputFileRef = useRef<HTMLInputElement>(null);
	const [file, setFile] = useState<File | null>(null);

	const handleChangeFile = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			setFile(e.target.files![0]);
			inputFileRef.current!.value = "";
		},
		[]
	);

	const onClick = useCallback((): void => {
		inputFileRef.current?.click();
	}, []);

	const onCancel = useCallback(() => {
		setFile(null);
	}, []);

	const { trigger, isMutating } = useSWRMutation(
		isVendor ? vendorMessageCacheKey(deal.id) : messageCacheKey(deal.id),
		async (_key: string, { arg }: { arg: File }) => {
			const postMethod = isVendor ? createFileForVendor : createFileForCompany;
			return await postMethod(deal.id, arg);
		}
	);

	const onSubmit = useCallback(async () => {
		const response = await trigger(file!);
		if (isSuccess(response)) {
			notify({ content: "ファイルを送信しました。", variant: "success" });
			setFile(null);
		} else {
			notify({
				content: `エラーが発生しました。 ${response.message ?? ""}`,
				variant: "error"
			});
		}
	}, [file, notify, trigger]);

	return (
		<>
			<input
				accept="image/*,video/*, .pdf, .xls, .xlsx"
				className={Styles.HideFilePostInput}
				onChange={handleChangeFile}
				ref={inputFileRef}
				type="file"
			/>
			<button
				className={Styles.MessageAttachmentButton}
				onClick={onClick}
				type="button"
			>
				<RiAttachment2 className={Styles.Icon} />
			</button>
			{file !== null ? (
				<Modalv2 isOpen onClose={onCancel} size="s">
					<ModalHeader title="ファイル送信" />
					<ModalBody>{file.name}を送信します。</ModalBody>
					<ModalFooter
						leftArea={
							<Button
								disabled={isMutating}
								onClick={onCancel}
								variant="secondary"
							>
								キャンセル
							</Button>
						}
						rightArea={
							<Button loading={isMutating} onClick={onSubmit} variant="primary">
								送信
							</Button>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
}
